import React, { useState, useEffect, useMemo } from 'react'
import { Spin, Modal, message, Checkbox, Alert, } from 'antd'
import { suggestOrdersSameEmbroideryInfo, reuseEmbroideryInfo } from '../../../../services/api/3dApi'

function SameEmbroideryInfoModal(props) {
    const {
        showSameEmbroideryInfoModal = false,
        handleHideSameEmbroideryInfoModal,
        marketplace,
        itemNumber,
        hideModalEmbroidery,
        setLoading,
        reloadItems,
        orderId,
        isNotOriginItemAndReuseItem,
    } = props

    const [visible, setVisible] = useState(false)
    const [loadingModal, setLoadingModal] = useState(false)
    const [itemList, setItemList] = useState([])
    const [selectedItems, setSelectedItems] = useState([])
    const [applyToAll, setApplyToAll] = useState(true)
    const [error, setError] = useState(null)
    const [invalidOrders, setInvalidOrders] = useState(new Set())

    const getStableId = (it) => {
        if (!it) return undefined
        if (it._id) return String(it._id)
        return `${it.barcode_number ?? 'no_barcode'}_${it.item_number ?? 'no_item'}`
    }

    const normalizedItems = useMemo(() => {
        if (!Array.isArray(itemList)) return []
        const seen = new Set()
        const out = []
        for (const it of itemList) {
            const id = getStableId(it)
            if (!id) continue
            if (seen.has(id)) continue
            seen.add(id)
            out.push(it)
        }
        return out
    }, [itemList])

    const [leftItems, rightItems] = useMemo(() => {
        const left = []
        const right = []
        normalizedItems.forEach((it, idx) => {
            if (idx % 2 === 0) left.push(it)
            else right.push(it)
        })
        return [left, right]
    }, [normalizedItems])

    const allIds = useMemo(() => normalizedItems.map(i => getStableId(i)), [normalizedItems])

    useEffect(() => {
        const normalizedIds = new Set(normalizedItems.map(getStableId))
        if (applyToAll) {
            setSelectedItems(normalizedItems.slice())
        } else {
            setSelectedItems(prev => prev.filter(p => normalizedIds.has(getStableId(p))))
        }
    }, [normalizedItems, applyToAll])

    const indeterminate = useMemo(() => {
        if (applyToAll) return false
        const selCount = selectedItems.length
        const total = allIds.length || 0
        return selCount > 0 && selCount < total
    }, [applyToAll, selectedItems, allIds])

    const fetchListItemWithSameEmbroidery = async () => {
        setLoading(true)
        setLoadingModal(true)
        setError(null)
        setInvalidOrders(new Set())
        try {
            const payload = {}
            if(isNotOriginItemAndReuseItem) payload.include_applied_orders = true

            const { data: res } = await suggestOrdersSameEmbroideryInfo(orderId, payload)
            const { success, data, message: mess, error: err} = res || {}
            if (!success) {
                throw new Error(mess || err || 'Đã có lỗi xảy ra khi lấy danh sách item')
            }
            hideModalEmbroidery()
            if (!(Array.isArray(data) && data.length > 0)) {
                return
            }
            setItemList(data || [])
            setVisible(true)
        } catch (err) {
            const errMsg = err?.message || 'Đã có lỗi xảy ra khi lấy danh sách item'
            message.error(errMsg)
            setError(errMsg)
            setItemList([])
        } finally {
            setLoadingModal(false)
            setLoading(false)
        }
    }

    useEffect(() => {
        if (showSameEmbroideryInfoModal) {
            fetchListItemWithSameEmbroidery()
        } else {
            // cleanup when modal hidden
            setItemList([])
            setSelectedItems([])
            setApplyToAll(true)
            setError(null)
            setInvalidOrders(new Set())
        }
    }, [showSameEmbroideryInfoModal, orderId])

    const toggleSingleItem = (item) => {
        const id = getStableId(item)
        if (!id) return

        if (applyToAll) {
            setApplyToAll(false)
            setSelectedItems(normalizedItems.filter(it => getStableId(it) !== id))
            return
        }
        setSelectedItems(prev => {
            const exists = prev.some(p => getStableId(p) === id)
            if (exists) {
                const next = prev.filter(p => getStableId(p) !== id)
                return next
            } else {
                const next = [...prev, item]
                if (next.length === normalizedItems.length) {
                    setApplyToAll(true)
                    return normalizedItems.slice()
                }
                return next
            }
        })
    }

    const handleToggleApplyAll = (e) => {
        const checked = e.target.checked
        setApplyToAll(checked)
        if (checked) {
            setSelectedItems(normalizedItems.slice())
        } else {
            setSelectedItems([])
        }
    }

    const normalizeInvalidToId = (entry) => {
        if (typeof entry === 'string' || typeof entry === 'number') return String(entry)
        if (entry && typeof entry === 'object') {
            const id = getStableId(entry)
            if (id) return id
            if (entry.id) return String(entry.id)
            if (entry._id) return String(entry._id)
            if (entry.item_number || entry.barcode_number) {
                return `${entry.barcode_number ?? 'no_barcode'}_${entry.item_number ?? 'no_item'}`
            }
        }
        return String(entry)
    }

    const handleReuseEmbroideryInfo = async () => {
        setLoadingModal(true)
        setError(null)
        setInvalidOrders(new Set())
        try {
            const applyItemsIds = applyToAll ? allIds : selectedItems.map(getStableId)
            if (!applyItemsIds || applyItemsIds.length === 0) {
                message.warning('Vui lòng chọn ít nhất 1 item để áp dụng.')
                setLoadingModal(false)
                return
            }

            const payload = {
                apply_items: applyItemsIds, // ids của order item để apply
                order_id: orderId, // id của order,
                all_items: allIds // lưu log danh sách tất cả item hiện tại có thể reuse
            }

            if(isNotOriginItemAndReuseItem) payload.allow_override = true
            const { data: res } = await reuseEmbroideryInfo(payload)
            const { success, message: msg, data = {} } = res || {}
            const { invalidOrders: invalid } = data || {}
            if (!success) {
                throw new Error(msg || 'Đã có lỗi khi áp dụng thông tin thiết kế thêu')
            }

            if (Array.isArray(invalid) && invalid.length > 0) {
                const invalidSet = new Set(invalid.map(i => i.order).map(normalizeInvalidToId))
                setInvalidOrders(invalidSet)

                const succeededCount = applyItemsIds.filter(id => !invalidSet.has(String(id))).length
                const failedCount = invalidSet.size

                message.error(`Có ${failedCount} item không áp dụng được.`)
                setError(`Có ${failedCount} item không áp dụng được.`)
                setLoadingModal(false)
                return
            }
            message.success('Áp dụng thông tin thiết kế thêu thành công.')
            reloadItems()
            if (handleHideSameEmbroideryInfoModal) handleHideSameEmbroideryInfoModal()
        } catch (err) {
            const msg = err?.message || 'Đã có lỗi khi áp dụng thông tin thiết kế thêu'
            setError(msg)
            message.error(msg)
        } finally {
            setLoadingModal(false)
        }
    }

    const _handleClose = () => {
        if (handleHideSameEmbroideryInfoModal) handleHideSameEmbroideryInfoModal()
        setVisible(false)
    }

    const orderTemplate = `#${marketplace}_${itemNumber}`

    return (
        <Modal
            className='SameEmbroideryInfoModal'
            title='Items chưa thiết kế có cùng desgin'
            visible={visible}
            okText='Apply'
            onCancel={_handleClose}
            onOk={handleReuseEmbroideryInfo}
            maskClosable={false}
            destroyOnClose
        >
            <label className='d-flex flex-column mb-3'>
                <i>Đây là danh sách item chưa thiết kế có cùng design với item (<span className='font-weight-bold'>{orderTemplate}</span>).</i>
                <i>Lựa chọn item muốn áp dụng chung thông tin thiết kế thêu.</i>
            </label>
            <Spin spinning={loadingModal} tip='Loading...'>
                <div>
                    <div className='mb-2 font-weight-bold' style={{ borderBottom: '1px solid #E9E9E9', paddingBottom: 8 }}>
                        <Checkbox
                            indeterminate={indeterminate}
                            checked={applyToAll}
                            onChange={handleToggleApplyAll}
                        >
                            Áp dụng cho tất cả item
                        </Checkbox>
                    </div>

                    <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 10, marginBottom: 10 }}>
                        {normalizedItems.length === 0 ? (
                            <div>Không có item nào có cùng thông tin thiết kế thêu.</div>
                        ) : (
                            <div style={{ display: 'flex', gap: 12 }}>
                                {/* Column 1 */}
                                <div style={{ flex: 1, padding: 8, borderRadius: 4 }}>
                                    {leftItems.map(item => {
                                        const id = getStableId(item)
                                        const { item_number, barcode_number } = item || {}
                                        const isInvalid = invalidOrders.has(id)
                                        const checked = applyToAll ? true : selectedItems.some(si => getStableId(si) === id)
                                        return (
                                            <div key={id} style={{ marginBottom: 8 }}>
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => toggleSingleItem(item)}
                                                >
                                                    <span style={{ color: isInvalid ? '#d93025' : undefined }}>
                                                        #{barcode_number}_{item_number}
                                                        {isInvalid && <span style={{ marginLeft: 8, fontSize: 12 }}>(failed)</span>}
                                                    </span>
                                                </Checkbox>
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Column 2 */}
                                <div style={{ flex: 1, padding: 8, borderRadius: 4 }}>
                                    {rightItems.map(item => {
                                        const id = getStableId(item)
                                        const { item_number, barcode_number } = item || {}
                                        const isInvalid = invalidOrders.has(id)
                                        const checked = applyToAll ? true : selectedItems.some(si => getStableId(si) === id)
                                        return (
                                            <div key={id} style={{ marginBottom: 8 }}>
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => toggleSingleItem(item)}
                                                >
                                                    <span style={{ color: isInvalid ? '#d93025' : undefined }}>
                                                        #{barcode_number}_{item_number}
                                                        {isInvalid && <span style={{ marginLeft: 8, fontSize: 12 }}>(failed)</span>}
                                                    </span>
                                                </Checkbox>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    {error && <div className='text-danger mb-2'>Lỗi: {error}</div>}

                    {/* invalid orders summary */}
                    {/* {invalidOrders.size > 0 && (
                        <Alert
                            type="error"
                            showIcon
                            message={`Có ${invalidOrders.size} item không áp dụng được`}
                            description={
                                <div style={{ maxHeight: 140, overflowY: 'auto' }}>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {normalizedItems
                                            .filter(it => invalidOrders.has(getStableId(it)))
                                            .map(it => {
                                                const id = getStableId(it)
                                                return (
                                                    <li key={id}>
                                                        #{it.barcode_number}_{it.item_number}
                                                    </li>
                                                )
                                            })}
                                    </ul>
                                </div>
                            }
                            style={{ marginBottom: 12 }}
                        />
                    )} */}
                </div>
            </Spin>
        </Modal>
    )
}

export default SameEmbroideryInfoModal